import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import API from "../lib/api";
import { Section } from "../components/Common";
import OfferCard from "../components/OfferCard";
import { toast } from "sonner";
import { errorMessage } from "../lib/errors";
import { ArrowLeft, MapPin, Globe, Phone, Mail, Sparkles, Calendar, MessageSquare, Tag } from "lucide-react";

export default function BusinessDetailPage() {
  const { slug } = useParams();
  const { t } = useTranslation();
  const [biz, setBiz] = useState(null);
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState(null); // 'inquiry' | 'booking' | null
  const [inq, setInq] = useState({ name: "", email: "", phone: "", message: "" });
  const [bk, setBk] = useState({ type: "consult", requested_at: "", note: "", name: "", email: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    setLoading(true);
    API.get(`/marketplace/listings/${slug}`)
      .then(async ({ data }) => {
        setBiz(data);
        if (data?.business_id) {
          try {
            const { data: o } = await API.get(`/business/offers/by-business/${data.business_id}?limit=6`);
            setOffers(o.items || []);
          } catch { setOffers([]); }
        }
      })
      .catch(() => setBiz(null))
      .finally(() => setLoading(false));
  }, [slug]);

  const submitInquiry = async (e) => {
    e.preventDefault();
    if (!inq.name || !inq.email || !inq.message) return toast.error(t("common.required_fields"));
    setSending(true);
    try {
      await API.post("/marketplace/inquiry", { business_id: biz.business_id, ...inq });
      toast.success(t("marketplace.inquiry.sent"));
      setInq({ name: "", email: "", phone: "", message: "" });
      setMode(null);
    } catch (e) { toast.error(errorMessage(e, t("marketplace.could_not_send"))); } finally { setSending(false); }
  };

  const submitBooking = async (e) => {
    e.preventDefault();
    if (!bk.name || !bk.email) return toast.error(t("common.required_fields"));
    setSending(true);
    try {
      await API.post("/marketplace/booking", { business_id: biz.business_id, ...bk });
      toast.success(t("marketplace.booking.sent"));
      setBk({ type: "consult", requested_at: "", note: "", name: "", email: "" });
      setMode(null);
    } catch (e) { toast.error(errorMessage(e, t("marketplace.could_not_send"))); } finally { setSending(false); }
  };

  if (loading) return <div className="app-container py-20 text-white/70">{t("common.loading")}</div>;
  if (!biz) return <div className="app-container py-20" data-testid="biz-not-found">{t("marketplace.business_not_found")}</div>;

  return (
    <div data-testid="page-biz-detail">
      <div className="relative h-[45vh] overflow-hidden">
        <img src={biz.image} alt={biz.name} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
        <div className="absolute bottom-0 inset-x-0 app-container pb-8">
          <Link to="/marketplace" className="btn-ghost inline-flex items-center gap-2 text-sm mb-4" data-testid="biz-back-to-marketplace"><ArrowLeft className="w-4 h-4" /> {t("marketplace.back_to_marketplace")}</Link>
          <div className="flex items-center gap-2 mb-2">
            <div className="eyebrow">{(biz.category || "").replace(/-/g, " ")}</div>
            {biz.tier === "premium" && <span className="chip chip-active"><Sparkles className="w-3 h-3" /> {biz.badge || t("marketplace.premium")}</span>}
          </div>
          <h1 className="font-display text-3xl md:text-5xl max-w-3xl leading-tight">{biz.name}</h1>
          <p className="text-white/80 mt-3 max-w-2xl">{biz.tagline}</p>
        </div>
      </div>

      <Section>
        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <div className="card-surface p-6">
              <div className="eyebrow mb-2">{t("marketplace.about")}</div>
              <p className="text-white/85 leading-relaxed body-copy">{biz.description}</p>
            </div>
            <div className="card-surface p-6" data-testid="biz-services">
              <div className="eyebrow mb-3">{t("marketplace.services_offered")}</div>
              <div className="flex flex-wrap gap-2">
                {(biz.services || []).map((s, i) => <span key={i} className="chip">{s}</span>)}
              </div>
            </div>

            {/* Phase 6.4 — inline offers for this business */}
            {offers.length > 0 && (
              <div data-testid="biz-offers-inline">
                <div className="eyebrow mb-3 flex items-center gap-2">
                  <Tag className="w-3.5 h-3.5 gold-text" />
                  {t("offers.business_offers_eyebrow")}
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  {offers.map((o) => (
                    <OfferCard
                      key={o.offer_id}
                      offer={{ ...o, business: { ...(o.business || {}), name: biz.name, slug: biz.slug, image: biz.image, city: biz.city, country: biz.country } }}
                      returnUrl={`/marketplace/business/${biz.slug}`}
                      testidPrefix="biz-offer"
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
          <aside className="space-y-4">
            <div className="card-surface p-6" data-testid="biz-meta">
              <div className="eyebrow mb-2">{t("marketplace.location")}</div>
              <div className="text-sm text-white/85 flex items-center gap-2"><MapPin className="w-4 h-4 gold-text" /> {biz.city}, {biz.country}</div>
              <div className="mt-4 eyebrow mb-2">{t("marketplace.languages")}</div>
              <div className="text-sm text-white/85 flex items-center gap-2 flex-wrap">
                <Globe className="w-4 h-4 gold-text" />
                {(biz.languages_offered || []).map((l) => l.toUpperCase()).join(" · ") || "—"}
              </div>
              <div className="mt-4 eyebrow mb-2">{t("marketplace.contact")}</div>
              {biz.website && <a href={biz.website} target="_blank" rel="noreferrer" className="text-sm text-white/85 flex items-center gap-2 hover:underline"><Globe className="w-4 h-4 gold-text" /> {biz.website.replace(/^https?:\/\//, "")}</a>}
              {biz.contact_phone && <div className="text-sm text-white/85 flex items-center gap-2 mt-1"><Phone className="w-4 h-4 gold-text" /> {biz.contact_phone}</div>}
              {biz.contact_email && <div className="text-sm text-white/85 flex items-center gap-2 mt-1"><Mail className="w-4 h-4 gold-text" /> {biz.contact_email}</div>}
              <div className="mt-6 grid gap-2">
                <button onClick={() => setMode("inquiry")} className="btn-primary text-sm inline-flex items-center gap-2 justify-center" data-testid="biz-inquire"><MessageSquare className="w-4 h-4" /> {t("marketplace.inquire")}</button>
                <button onClick={() => setMode("booking")} className="btn-secondary text-sm inline-flex items-center gap-2 justify-center" data-testid="biz-book"><Calendar className="w-4 h-4" /> {t("marketplace.book")}</button>
              </div>
            </div>

            {mode === "inquiry" && (
              <form onSubmit={submitInquiry} className="card-surface p-6 grid gap-3" data-testid="inquiry-form">
                <div className="font-display text-lg">{t("marketplace.inquiry.title")}</div>
                <input value={inq.name} onChange={(e) => setInq({ ...inq, name: e.target.value })} placeholder={t("marketplace.inquiry.name")} required className="bg-[#0F1116] border border-white/10 rounded-lg px-4 py-3 text-sm" data-testid="inq-name" />
                <input type="email" value={inq.email} onChange={(e) => setInq({ ...inq, email: e.target.value })} placeholder={t("marketplace.inquiry.email")} required className="bg-[#0F1116] border border-white/10 rounded-lg px-4 py-3 text-sm" data-testid="inq-email" />
                <input value={inq.phone} onChange={(e) => setInq({ ...inq, phone: e.target.value })} placeholder={t("marketplace.inquiry.phone")} className="bg-[#0F1116] border border-white/10 rounded-lg px-4 py-3 text-sm" />
                <textarea value={inq.message} onChange={(e) => setInq({ ...inq, message: e.target.value })} placeholder={t("marketplace.inquiry.message")} rows={4} required className="bg-[#0F1116] border border-white/10 rounded-lg px-4 py-3 text-sm" data-testid="inq-msg" />
                <button disabled={sending} className="btn-primary text-sm" data-testid="inq-submit">{sending ? "…" : t("marketplace.inquiry.submit")}</button>
              </form>
            )}
            {mode === "booking" && (
              <form onSubmit={submitBooking} className="card-surface p-6 grid gap-3" data-testid="booking-form">
                <div className="font-display text-lg">{t("marketplace.booking.title")}</div>
                <select value={bk.type} onChange={(e) => setBk({ ...bk, type: e.target.value })} className="bg-[#0F1116] border border-white/10 rounded-lg px-3 py-2 text-sm" data-testid="bk-type">
                  <option value="consult">{t("marketplace.booking.type_consult")}</option>
                  <option value="session">{t("marketplace.booking.type_session")}</option>
                  <option value="workshop">{t("marketplace.booking.type_workshop")}</option>
                </select>
                <input type="datetime-local" value={bk.requested_at} onChange={(e) => setBk({ ...bk, requested_at: e.target.value })} className="bg-[#0F1116] border border-white/10 rounded-lg px-3 py-2 text-sm" data-testid="bk-when" />
                <input value={bk.name} onChange={(e) => setBk({ ...bk, name: e.target.value })} placeholder={t("marketplace.inquiry.name")} required className="bg-[#0F1116] border border-white/10 rounded-lg px-4 py-3 text-sm" data-testid="bk-name" />
                <input type="email" value={bk.email} onChange={(e) => setBk({ ...bk, email: e.target.value })} placeholder={t("marketplace.inquiry.email")} required className="bg-[#0F1116] border border-white/10 rounded-lg px-4 py-3 text-sm" data-testid="bk-email" />
                <textarea value={bk.note} onChange={(e) => setBk({ ...bk, note: e.target.value })} placeholder={t("marketplace.booking.note")} rows={3} className="bg-[#0F1116] border border-white/10 rounded-lg px-4 py-3 text-sm" />
                <button disabled={sending} className="btn-primary text-sm" data-testid="bk-submit">{sending ? "…" : t("marketplace.booking.submit")}</button>
              </form>
            )}
          </aside>
        </div>
      </Section>
    </div>
  );
}
